import logging from '@sstdev/lib_logging';
import { add, startOfDay, endOfDay } from 'date-fns';
import nativeOnlyProperties from '../nativeOnlyProperties';
import { overrideHostname as _overrideHostname, asyncHostname, resolveHostnameOverride } from './getHostname';
import { getEnvironmentForHostname } from './getEnvironment';
import timeout from './timeouts';
import getGlobalObject from './getGlobalObject';
import uiRuntimeConfig from '../uiRuntimeConfig';
const global = getGlobalObject();
/**
 * This is read only after it is initialized to prevent accidental updates of
 * global config, but it can be intentionally reinitialized.  See overrideHostname()
 * below.
 */

// productionBuild just means webpack built it for production (minified, etc.)
// - it could still be running in dev, staging, etc.
const productionBuild = process.env.NODE_ENV === 'production';
// @ts-ignore
const skipSentryLogging = (__SKIP_SENTRY_LOGGING__ ?? false) || !productionBuild; // For testing purposes, we can skip Sentry logging.
//const productionBuild = true; // Testing with dev url.

let globalConfig;
let initialized = false;
/** @type {{ beforeChange: (environment: string) => void | Promise<unknown>, afterChange: () => void | Promise<unknown> }} */
let hostnameChangeObserver = { beforeChange() {}, afterChange() {} };
/**
 * @type {number} GroupNumber - default to 0 (mainly for react native).
 */
let _groupNumber = 0;
/**
 * @type {Array<string>} FeatureFlags
 */
let _featureFlags = [];

/**
 * @type {{
 *  maxShortLength:number,
 *  shortStrategy:string,
 *  longStrategy:string,
 *  displayStrategy:string
 * } | undefined} RfidConfig
 */
let _rfid;

/**
 * @typedef {import('@sstdev/types').TenantSettings | undefined} TenantSettings
 */

/**
 * @type {TenantSettings}
 */
let _tenantSettings;

function initialize() {
    const identityConfig = uiRuntimeConfig.getIdentityConfig({ productionBuild });
    const { brand, environment, host, hostname, isDeployed, port, protocol, protocolSocket } = identityConfig;
    const auth0Config = uiRuntimeConfig.getAuth0Config({ brand, environment, globalObject: global });
    const runtimeConfig = uiRuntimeConfig.getConfig({
        globalObject: global,
        sessionFeatureFlags: _featureFlags
    });

    // Keep the long-lived globalConfig() shape stable while individual runtime
    // knobs move behind lib/config definitions and providers.
    let config = {
        actionElementDebounceMilliseconds: runtimeConfig.actionElementDebounceMilliseconds,
        auth0: {
            ...(auth0Config.AUTH0_AUDIENCE ? { AUTH0_AUDIENCE: auth0Config.AUTH0_AUDIENCE } : {}),
            AUTH0_CLIENT_ID: auth0Config.AUTH0_CLIENT_ID,
            AUTH0_DOMAIN: auth0Config.AUTH0_DOMAIN,
            AUTH0_CONNECTION_NAME: auth0Config.AUTH0_CONNECTION_NAME
        },
        auth0RefreshTokenAbsoluteLifetimeDays: runtimeConfig.auth0RefreshTokenAbsoluteLifetimeDays,
        backgroundSyncBatchSize: runtimeConfig.backgroundSyncBatchSize,
        bluetooth: {
            scanPeriod: 5000
        },
        brand,
        defaultDateRange: {
            endDateTime: endOfDay(new Date()),
            startDateTime: startOfDay(add(new Date(), { months: -3 }))
        },
        defaultMetaFields: ['_id'],
        /*
        Essentially, the code being disabled with "disableHeaderHiding" hides the header if a use scrolls toward the
        bottom of the page and displays it again if the user scrolls toward the top. It works fairly well in many cases
        to provide additional real-estate, and you may have seen similar designs in other apps. When it is working
        perfectly, you hardly notice it is happening.
        Unfortunately, there are cases where the touch event is getting intercepted and the header remains hidden, or
        sometimes the user will drag down and the page refreshes instead of displaying the header - which is why this
        disable flag exists.
        Initially, I created a branch that simply removes all this code and I was ready to submit it in a PR, but then
        I thought about how useful this feature actually is when working correctly and decided to go the route of
        disabling the functionality instead. It may be that we can disabled the "refresh on drag down" touch experience
        (see bottom of page here: https://developer.mozilla.org/en-US/docs/Web/CSS/overscroll-behavior) and fix the
        touch event interceptions.
        For more context, see this PR: https://sstdev.visualstudio.com/Backbone/_git/silo_ui/pullrequest/3705
        */
        disableHeaderHiding: true,
        environment,
        exportTypes: [
            {
                _id: '1',
                bookType: 'xlsx',
                description: 'Excel 2007+ XML (XLSX)',
                fileExt: '.xlsx'
            },
            {
                _id: '2',
                bookType: 'biff8',
                description: 'Excel 97-2004 Workbook (XLS)',
                fileExt: '.xls'
            },
            {
                _id: '3',
                bookType: 'csv',
                description: 'Comma Separated Values (CSV)',
                fileExt: '.csv'
            },
            {
                _id: '4',
                bookType: 'html',
                description: 'HTML Document',
                fileExt: '.html'
            }
        ],
        featureFlags: runtimeConfig.featureFlags,
        fileTypes: { image: 'image' },
        getRecordsDebounceMilliseconds: runtimeConfig.getRecordsDebounceMilliseconds,
        getRecordsThrottleMilliseconds: runtimeConfig.getRecordsThrottleMilliseconds,
        groupNumber: _groupNumber,
        hostname,
        host,
        isDeployed,
        loadingTimeout: runtimeConfig.loadingTimeout,
        logRelease: 'silo_ui-web@' + __PACKAGE_VERSION__,
        // 0: Error, 1: Warning, 2: Info (default), 3: Debug
        logType: skipSentryLogging || environment !== 'prod'
            ? logging.outputType.CONSOLE
            : logging.outputType.CONSOLE + logging.outputType.SENTRY,
        maxDeletePerTruncationPass: runtimeConfig.maxDeletePerTruncationPass,
        maxRecordsInLimitedSizeCollection: runtimeConfig.maxRecordsInLimitedSizeCollection,
        maxSecondsOfflineWithoutResync: runtimeConfig.maxSecondsOfflineWithoutResync,
        minFullTextSearchTermLength: 3,
        minLogLevel: productionBuild ? 1 : 3,
        networkErrorRetryMilliseconds: runtimeConfig.networkErrorRetryMilliseconds,
        notificationTimeout: runtimeConfig.notificationTimeout,
        notificationTimeoutFormError: runtimeConfig.notificationTimeoutFormError,
        offlineSessionExpirationDays: runtimeConfig.offlineSessionExpirationDays,
        onlineSearchDebounceMilliseconds: runtimeConfig.onlineSearchDebounceMilliseconds,
        pageSizeForLimitSyncSizeRelations: runtimeConfig.pageSizeForLimitSyncSizeRelations,
        port,
        prohibitedPreviewVerbs: ['cancel'],
        protocol,
        protocolSocket,
        rfid: _rfid,
        searchDebounceMilliseconds: runtimeConfig.searchDebounceMilliseconds,
        sensorScanIntervalMilliseconds: runtimeConfig.sensorScanIntervalMilliseconds,
        resyncRandomDelaySeconds: runtimeConfig.resyncRandomDelaySeconds,
        socketEndpointLastNode: 'socket.iov3', // v1 & v2 = 'socket.io',
        tenantSettings: _tenantSettings,
        // Going to start grouping timeouts in here
        timeout,
        useOauthAuthentication: false,
        useTempSecurityEndpoint: false,
        validActionResults: ['FAILURE', 'SUCCESS', 'UNNECESSARY', 'COMMIT', 'ROLLBACK'],
        validActionVerbs: [
            'cancel',
            'change',
            'clear',
            'clearSelection',
            'close',
            'confirm',
            'copy',
            'edit',
            'export',
            'filter',
            // not for database or http data access (use load for those) - for acquiring a resource or value from another part of the application
            'get',
            'import',
            'login',
            'logout',
            'load',
            'mark',
            'new',
            'notify',
            'open',
            'persist',
            'pop',
            'purge',
            'refresh',
            'register',
            'remove',
            'reset',
            'resize',
            'save',
            'select',
            'update',
            'setFilter',
            'startup',
            'stop',
            'submit',
            'upsert',
            'validate',
            'view'
        ],
        validHeaderProperties: [
            'modifiedBy',
            'modifiedTime',
            'createdBy',
            'createdTime',
            'deletedBy',
            'deletedTime',
            'conflict.status',
            'conflict.reason',
            'conflict.winnerId',
            'correlationId'
        ]
    };

    config = {
        ...config,
        ...nativeOnlyProperties({
            logRelease: 'siloUiNative@' + __PACKAGE_VERSION__,
            logType: productionBuild
                ? logging.outputType.BRIDGE
                : logging.outputType.CONSOLE + logging.outputType.BRIDGE,
            requiresSdStorage: true,
            requiresBluetooth: true,
            bluetooth: {
                scanPeriod: 5000
            }
        })
    };

    globalConfig = config;
    // Make available outside lib_ui-services
    globalConfig.overrideHostname = overrideHostname;
    globalConfig.setHostnameChangeObserver = observer => { hostnameChangeObserver = observer; };
    globalConfig.reInitialize = reInitialize;
    globalConfig.setGroupNumber = setGroupNumber;
    globalConfig.asyncHostname = asyncHostname;
    globalConfig.setFeatureFlags = setFeatureFlags;
    globalConfig.setRfidConfig = setRfidConfig;
    globalConfig.setTenantSettings = setTenantSettings;
    Object.freeze(globalConfig);

    initialized = true;
}

function getGlobalConfig() {
    if (!initialized) {
        initialize();
    }
    return globalConfig;
}

export default getGlobalConfig;
export function reInitialize() {
    initialized = false;
}
export function overrideHostname(hostname, beforeRestart) {
    const destination = getEnvironmentForHostname(resolveHostnameOverride(hostname));
    // Stop telemetry before the new identity becomes visible to any consumer.
    if (destination !== 'prod') logging.setEnvironment(undefined);
    const stopped = hostnameChangeObserver.beforeChange(destination);
    const applyHostname = () => {
        const savedHostname = _overrideHostname(hostname);
        reInitialize();
        return Promise.resolve(savedHostname)
            .then(() => beforeRestart?.())
            .then(() => hostnameChangeObserver.afterChange());
    };
    // Web overrides remain synchronous. Native waits for any SDK startup/shutdown in flight.
    return stopped ? stopped.then(applyHostname) : applyHostname();
}
export function setGroupNumber(groupNumber) {
    _groupNumber = groupNumber;
    reInitialize();
}

export function setFeatureFlags(featureFlags) {
    _featureFlags = featureFlags;
    reInitialize();
}
export function setRfidConfig(rfidConfig) {
    _rfid = rfidConfig;
    reInitialize();
}

/**
 * @param {TenantSettings} tenantSettings
 */
export function setTenantSettings(tenantSettings) {
    _tenantSettings = tenantSettings;
    reInitialize();
}
